import React, { createContext, useContext, useRef, useState } from "react";

const KEY = "pmt_wishlist_v1";
const WishlistContext = createContext(null);

function readStored() {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeStored(items) {
  // Replace the persisted snapshot, rather than allowing any old key/value
  // to survive a mutation. Storage writes stay synchronous with React updates.
  localStorage.removeItem(KEY);
  localStorage.setItem(KEY, JSON.stringify(items));
}

export function WishlistProvider({ children }) {
  const itemsRef = useRef(null);
  if (itemsRef.current === null) {
    itemsRef.current = readStored();
  }
  const [items, setItems] = useState(itemsRef.current);

  // Persist every mutation synchronously in commitItems. Avoid an effect that
  // writes the rendered state back to storage: an effect queued with an older
  // render can run after a click and overwrite a newer removal/clear operation.
  const commitItems = (nextItems) => {
    itemsRef.current = nextItems;
    writeStored(nextItems);
    setItems(nextItems);
  };

  const makeKey = (type, id) => `${type}:${id}`;

  const isWishlisted = (type, id) =>
    items.some((item) => item.key === makeKey(type, id));

  const toggleWishlist = (item) => {
    const key = makeKey(item.type, item.id);
    // Read the latest persisted value before mutating so a stale provider
    // snapshot cannot reintroduce an item that was just removed elsewhere.
    const currentItems = readStored();
    itemsRef.current = currentItems;

    if (currentItems.some((saved) => saved.key === key)) {
      commitItems(currentItems.filter((saved) => saved.key !== key));
      return;
    }

    commitItems([
      ...currentItems,
      {
        ...item,
        key,
        savedAt: Date.now(),
      },
    ]);
  };

  const removeWishlist = (type, id) => {
    const key = makeKey(type, id);
    // Use the provider's latest committed snapshot, then persist the exact
    // result before React schedules a render. Reading storage here can pick up
    // an out-of-date snapshot during a rapid remove/clear interaction.
    commitItems(itemsRef.current.filter((item) => item.key !== key));
  };

  const clearWishlist = () => {
    // Keep clear on the same synchronous persistence path as every mutation.
    commitItems([]);
  };

  return (
    <WishlistContext.Provider
      value={{
        items,
        count: items.length,
        isWishlisted,
        toggleWishlist,
        removeWishlist,
        clearWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    throw new Error("useWishlist must be inside WishlistProvider");
  }
  return ctx;
}
